import { Component, EventEmitter, Input, OnInit, Output, Inject } from '@angular/core';
import { Project } from '../../../api/models/project';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ProjectFormDialogComponent, ProjectFormData } from '../project-form-dialog/project-form-dialog.component';
import { AdminSandbox } from '../../admin.sandbox';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface DialogData {
  projectName: string;
}

@Component({
  selector: 'app-projects-list',
  templateUrl: './projects-list.component.html',
  styleUrls: ['./projects-list.component.css']
})
export class ProjectsListComponent implements OnInit {

  @Input() projects: Project[];
  @Output() projectSelected: EventEmitter<Project> = new EventEmitter();
  @Output() projectEdition: EventEmitter<Project> = new EventEmitter();
  @Output() projectDeleted: EventEmitter<Project> = new EventEmitter();
  @Output() projectUpdated: EventEmitter<Project> = new EventEmitter();
  @Output() projectCreation: EventEmitter<void> = new EventEmitter();
  @Output() activeChange: EventEmitter<{project: Project, active: boolean}> = new EventEmitter();

  displayedColumns: string[] = ['id', 'name', 'folder', 'active', 'actions'];
  dataSource: MatTableDataSource<Project>;

  constructor(private dialog: MatDialog, private router: Router, private sandbox: AdminSandbox, private snackBar: MatSnackBar) { }

  ngOnInit() {
    // Trigger loading of project list only when this component is initialized.
    this.sandbox.loadProjects();
    this.sandbox.loadUsers();
    // Subscribe to the projects from the store (assuming getProjects selector is set up)
    this.sandbox.getProjects().subscribe(projects => {
      this.projects = projects;
      this.dataSource = new MatTableDataSource(projects);
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  onProjectSelected(project: Project) {
    this.projectSelected.emit(project);
  }

  onProjectEdition(project: Project) {
    this.projectEdition.emit(project);
  }

  onProjectDeleted(project: Project) {
    this.projectDeleted.emit(project);
    this.projects = this.projects.filter(p => p.id !== project.id);
    this.dataSource.data = this.projects;
  }

  onSyncCameras(): void {
    this.sandbox.syncCameras().subscribe(() => {
      console.log('Cameras synced successfully');
      this.snackBar.open('Cameras synced successfully', 'OK', { duration: 3000 });
    }, error => {
      console.error('Error syncing cameras:', error);
    });
  }

  onNewProject(): void {
    const dialogRef = this.dialog.open(ProjectFormDialogComponent, {
      width: '400px',
      data: {}
    });

    dialogRef.afterClosed().subscribe((result: ProjectFormData) => {
      if (result) {
        // Remove numberOfCameras and add beginDate with today's date
        const modifiedResult = {
          ...result,
          beginDate: new Date().toISOString() // Format: 2025-04-22T00:00:00.000Z
        };
        const numberOfCameras = modifiedResult.numberOfCameras;
        delete modifiedResult.numberOfCameras;
        this.sandbox.createProject(modifiedResult).subscribe((newProject: Project) => {
          this.projects.push(newProject);
          this.sandbox.createCameras(newProject.id, numberOfCameras).subscribe(() => {
            console.log('Cameras created successfully');
            this.router.navigate(['/admin/formProjects']);
        });
      });
    }
  });
}

  onActiveChange(project: Project, event: any) {
    project.active = event.checked;
    this.projectUpdated.emit(project);
  }

  openDialog(project): void {
    const dialogRef = this.dialog.open(ConfirmDeletionDialog, {
      width: '400px',
      data: { projectName: project.name }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.onProjectDeleted(project);
      }
    });
  }

}

@Component({
  selector: 'confirm-dialog',
  templateUrl: 'confirm-dialog.html',
})
export class ConfirmDeletionDialog {

  constructor(
    public dialogRef: MatDialogRef<ConfirmDeletionDialog>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData) {}

  onNoClick(): void {
    this.dialogRef.close();
  }

  onYesClick(): void {
    this.dialogRef.close(true);
  }

}
